import React, { useEffect, useRef, useState } from 'react';
import {
  Bold,
  Braces,
  Italic,
  Link2,
  List,
  ListOrdered,
  Quote,
  RemoveFormatting,
  Underline,
} from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { sanitizeProductHtml } from '../lib/productHtml';

const toolbarButtonClass = 'h-8 w-8 p-0 text-slate-600 hover:text-slate-950';

export default function HtmlRichTextEditor({
  value = '',
  onChange,
  placeholder = 'Enter formatted content…',
  minHeight = 150,
  ariaLabel = 'HTML rich text editor',
}) {
  const editorRef = useRef(null);
  const [sourceMode, setSourceMode] = useState(false);

  useEffect(() => {
    if (sourceMode || !editorRef.current || document.activeElement === editorRef.current) return;
    const safeValue = sanitizeProductHtml(value);
    if (editorRef.current.innerHTML !== safeValue) editorRef.current.innerHTML = safeValue;
  }, [value, sourceMode]);

  const emitEditorValue = () => {
    if (!editorRef.current) return;
    onChange(sanitizeProductHtml(editorRef.current.innerHTML));
  };

  const execute = (command, commandValue = null) => {
    if (sourceMode) return;
    editorRef.current?.focus();
    document.execCommand(command, false, commandValue);
    emitEditorValue();
  };

  const setBlock = (event) => {
    const tag = event.target.value;
    if (tag) execute('formatBlock', tag);
    event.target.value = '';
  };

  const addLink = () => {
    if (sourceMode) return;
    const href = window.prompt('Enter a link beginning with https://, mailto:, tel:, / or #');
    if (!href) return;
    execute('createLink', href.trim());
  };

  const toggleSourceMode = () => {
    if (sourceMode) {
      const safeValue = sanitizeProductHtml(value);
      onChange(safeValue);
      setSourceMode(false);
      requestAnimationFrame(() => {
        if (editorRef.current) editorRef.current.innerHTML = safeValue;
      });
      return;
    }
    emitEditorValue();
    setSourceMode(true);
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const html = event.clipboardData.getData('text/html');
    const text = event.clipboardData.getData('text/plain');
    if (html) document.execCommand('insertHTML', false, sanitizeProductHtml(html));
    else document.execCommand('insertText', false, text);
    emitEditorValue();
  };

  return (
    <div className="overflow-hidden rounded-md border border-slate-200 bg-white focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500">
      <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 bg-slate-50 px-2 py-1.5">
        <select
          aria-label="Paragraph style"
          defaultValue=""
          disabled={sourceMode}
          onChange={setBlock}
          className="h-8 rounded border border-slate-200 bg-white px-2 text-xs text-slate-700 disabled:opacity-50"
        >
          <option value="" disabled>Style</option>
          <option value="p">Paragraph</option>
          <option value="h2">Heading 2</option>
          <option value="h3">Heading 3</option>
          <option value="h4">Heading 4</option>
        </select>

        <span className="mx-1 h-5 w-px bg-slate-200" />
        <Button type="button" variant="ghost" size="icon" className={toolbarButtonClass} disabled={sourceMode} onMouseDown={(e) => e.preventDefault()} onClick={() => execute('bold')} title="Bold"><Bold size={15} /></Button>
        <Button type="button" variant="ghost" size="icon" className={toolbarButtonClass} disabled={sourceMode} onMouseDown={(e) => e.preventDefault()} onClick={() => execute('italic')} title="Italic"><Italic size={15} /></Button>
        <Button type="button" variant="ghost" size="icon" className={toolbarButtonClass} disabled={sourceMode} onMouseDown={(e) => e.preventDefault()} onClick={() => execute('underline')} title="Underline"><Underline size={15} /></Button>
        <Button type="button" variant="ghost" size="icon" className={toolbarButtonClass} disabled={sourceMode} onMouseDown={(e) => e.preventDefault()} onClick={() => execute('insertUnorderedList')} title="Bulleted list"><List size={15} /></Button>
        <Button type="button" variant="ghost" size="icon" className={toolbarButtonClass} disabled={sourceMode} onMouseDown={(e) => e.preventDefault()} onClick={() => execute('insertOrderedList')} title="Numbered list"><ListOrdered size={15} /></Button>
        <Button type="button" variant="ghost" size="icon" className={toolbarButtonClass} disabled={sourceMode} onMouseDown={(e) => e.preventDefault()} onClick={() => execute('formatBlock', 'blockquote')} title="Quote"><Quote size={15} /></Button>
        <Button type="button" variant="ghost" size="icon" className={toolbarButtonClass} disabled={sourceMode} onMouseDown={(e) => e.preventDefault()} onClick={addLink} title="Add link"><Link2 size={15} /></Button>
        <Button type="button" variant="ghost" size="icon" className={toolbarButtonClass} disabled={sourceMode} onMouseDown={(e) => e.preventDefault()} onClick={() => execute('removeFormat')} title="Remove formatting"><RemoveFormatting size={15} /></Button>

        <Button
          type="button"
          variant={sourceMode ? 'default' : 'outline'}
          size="sm"
          className="ml-auto h-8 gap-1.5 px-2.5 text-xs"
          onClick={toggleSourceMode}
          title="Edit source HTML"
        >
          <Braces size={14} /> HTML
        </Button>
      </div>

      {sourceMode ? (
        <Textarea
          aria-label={`${ariaLabel} source`}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onBlur={() => onChange(sanitizeProductHtml(value))}
          placeholder="<p>Enter safe product HTML here…</p>"
          className="resize-y rounded-none border-0 font-mono text-xs leading-6 shadow-none focus-visible:ring-0"
          style={{ minHeight }}
        />
      ) : (
        <div
          ref={editorRef}
          role="textbox"
          aria-label={ariaLabel}
          aria-multiline="true"
          contentEditable
          suppressContentEditableWarning
          data-placeholder={placeholder}
          onFocus={() => document.execCommand('defaultParagraphSeparator', false, 'p')}
          onInput={emitEditorValue}
          onBlur={emitEditorValue}
          onPaste={handlePaste}
          className="max-h-[360px] overflow-y-auto px-3 py-2.5 text-sm leading-6 text-slate-800 outline-none empty:before:pointer-events-none empty:before:text-slate-400 empty:before:content-[attr(data-placeholder)] [&_a]:text-indigo-600 [&_a]:underline [&_blockquote]:border-l-4 [&_blockquote]:border-slate-300 [&_blockquote]:pl-4 [&_h2]:my-2 [&_h2]:text-xl [&_h2]:font-semibold [&_h3]:my-2 [&_h3]:text-lg [&_h3]:font-semibold [&_h4]:my-2 [&_h4]:font-semibold [&_ol]:ml-5 [&_ol]:list-decimal [&_p]:my-1 [&_ul]:ml-5 [&_ul]:list-disc"
          style={{ minHeight }}
        />
      )}
    </div>
  );
}
